import React, { useEffect, useState } from 'react';
import {
    Avatar, Box, Chip, CircularProgress, IconButton, InputAdornment,
    List, ListItem, ListItemAvatar, ListItemText, TextField, Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useTelegram } from 'hooks/useTelegram';
import StickersService from 'api/stickers/stickers.service';
import { StickerModel } from 'api/stickers/models/sticker.model';

const StickersPage = () => {
    const { tg } = useTelegram();
    const initData: string = tg.initData;

    const [stickers, setStickers] = useState<StickerModel[]>([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingTags, setEditingTags] = useState('');

    useEffect(() => {
        StickersService.getAll(initData)
            .then(setStickers)
            .finally(() => setLoading(false));
    }, [initData]);

    const startEdit = (sticker: StickerModel) => {
        setEditingId(sticker.id);
        setEditingTags(sticker.tags.join(' '));
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditingTags('');
    };

    const saveEdit = async (id: number) => {
        const tags = editingTags
            .split(/[\s,]+/)
            .map(t => t.trim().replace(/^#/, ''))
            .filter(Boolean);

        await StickersService.updateTags(id, initData, tags);
        setStickers(prev =>
            prev.map(s => s.id === id ? { ...s, tags } : s)
        );
        cancelEdit();
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Видалити стікер?')) return;
        await StickersService.delete(id, initData);
        setStickers(prev => prev.filter(s => s.id !== id));
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" mt={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (stickers.length === 0) {
        return (
            <Box textAlign="center" mt={4}>
                <Typography color="text.secondary">Збережених стікерів немає</Typography>
            </Box>
        );
    }

    return (
        <List disablePadding>
            {stickers.map(sticker => (
                <ListItem
                    key={sticker.id}
                    divider
                    alignItems="flex-start"
                    secondaryAction={
                        editingId === sticker.id ? (
                            <>
                                <IconButton size="small" onClick={() => saveEdit(sticker.id)}>
                                    <CheckIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={cancelEdit}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </>
                        ) : (
                            <>
                                <IconButton size="small" onClick={() => startEdit(sticker)}>
                                    <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={() => handleDelete(sticker.id)}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </>
                        )
                    }
                >
                    {sticker.hasThumbnail && (
                        <ListItemAvatar>
                            <Avatar
                                src={StickersService.thumbnailUrl(sticker.id, initData)}
                                variant="rounded"
                                sx={{ width: 48, height: 48 }}
                            />
                        </ListItemAvatar>
                    )}
                    <ListItemText
                        primary={
                            <Box display="flex" alignItems="center" gap={0.5}>
                                {sticker.emoji && <span>{sticker.emoji}</span>}
                                <Typography variant="body2" color="text.secondary" noWrap>
                                    {sticker.setName ?? sticker.type}
                                </Typography>
                            </Box>
                        }
                        secondary={
                            editingId === sticker.id ? (
                                <TextField
                                    size="small"
                                    fullWidth
                                    value={editingTags}
                                    onChange={e => setEditingTags(e.target.value)}
                                    placeholder="теги через пробіл"
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">#</InputAdornment>
                                    }}
                                    sx={{ mt: 0.5 }}
                                    autoFocus
                                />
                            ) : (
                                <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                                    {sticker.tags.map(tag => (
                                        <Chip key={tag} label={`#${tag}`} size="small" />
                                    ))}
                                </Box>
                            )
                        }
                    />
                </ListItem>
            ))}
        </List>
    );
};

export default StickersPage;
